import { Text } from '@expressable/syntax-ds';
import { MapPinPlus, Medal, ShieldCheck, Star } from '@phosphor-icons/react';
import { useContentfulTherapist } from 'components/ContentfulTherapistCard/useContentfulTherapist';
import { useContentfulTherapistAverageScore } from 'components/ContentfulTherapistCard/useContentfulTherapistAverageScore';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twMerge } from 'tailwind-merge';
import { StateAbbrev } from 'utils/states';
import { PlaceholderImg } from '../LoadingImage/LoadingImage';
import { AboutTheTherapist } from 'components/CalendarPicker/AboutTheTherapist';

export type TherapistSummaryProps = {
  therapistName: string;
  stateCode: StateAbbrev;
  clinicalAreas: string[];
  insuranceName?: string;
  shouldShowProfile?: boolean;

  cta?: React.ReactNode;
};

export const ContentfulTherapistSummary: FC<TherapistSummaryProps> = props => {
  const { therapistName, shouldShowProfile, cta } = props;
  const { data: therapistData } = useContentfulTherapist(therapistName);
  const { t } = useTranslation();
  const { data: reviewData } = useContentfulTherapistAverageScore(therapistName);
  const score = reviewData?.score;
  const reviewCount = reviewData?.reviewCount;
  const { signupShowReviewCount } = useFlags();

  let formattedScore;
  if (score !== undefined) {
    formattedScore = Number.isInteger(score) ? score?.toFixed(1) : score?.toFixed(2);
  }

  // reviewCount will never be 0, but it could be undefined
  const shouldShowReviewCount = signupShowReviewCount && reviewCount !== undefined;

  return (
    <div className="flex flex-col gap-e24">
      <div className="flex gap-e24 items-center">
        {therapistData && (
          <PlaceholderImg
            className="rounded-full size-e80 mobile-d:size-e64"
            src={therapistData.fields.picture.fields.file?.url as string}
          />
        )}
        <div>
          <Text variant="h4" className="mb-e4">
            {therapistName}
          </Text>
          <Text variant="body-xs" className="text-gray-600 mb-e12">
            {t('directSignup.eval.schedule.therapistCard.subtitle')}
          </Text>

          <BadgesSection {...props} className="mobile-d:hidden" />
        </div>
      </div>
      <BadgesSection {...props} className="tablet:hidden" />

      {shouldShowProfile && formattedScore && (
        <div>
          <Text variant="h6" className="inline-flex gap-e8 mb-e4">
            {formattedScore}
            <Star weight="fill" size={18} className="shrink-0 text-marigold-800" />
          </Text>
          <Text variant="hint">
            {t('directSignup.eval.schedule.providerProfile.avgRating')}{' '}
            {shouldShowReviewCount &&
              t('directSignup.eval.schedule.providerProfile.reviewCount', { count: reviewCount })}
          </Text>
        </div>
      )}

      {props.children}

      {shouldShowProfile && <AboutTheTherapist therapistName={therapistName} />}

      {cta}
    </div>
  );
};

const BadgesSection = ({
  className,
  stateCode,
  clinicalAreas: clinicalSpecialties,
  insuranceName,
}: TherapistSummaryProps & { className?: string }) => {
  const { t } = useTranslation();

  return (
    <div className={twMerge('flex gap-e8 flex-wrap', className)}>
      <Pill>
        <MapPinPlus weight="fill" className="size-e12 text-purple-500" />
        {t('directSignup.eval.schedule.therapistCard.licensedIn', { stateCode })}
      </Pill>
      {clinicalSpecialties.map(area => (
        <Pill key={area}>
          <Medal weight="fill" className="size-e12 text-purple-500" />
          {area}
        </Pill>
      ))}

      {insuranceName && (
        <Pill>
          <ShieldCheck weight="fill" className="size-e12 text-purple-500" />
          {insuranceName}
        </Pill>
      )}
    </div>
  );
};

const Pill: FC = ({ children }) => {
  return (
    <Text
      variant="body-xs"
      className="flex gap-e2 py-e4 px-e6 rounded-full bg-purple-50 text-warm-gray-800 items-center"
    >
      {children}
    </Text>
  );
};
